import type { KeyboardEvent, ReactNode } from "react";
import { editFieldText } from "@/lib/titleAuthors";
import { testIds } from "@/testIds";
import { chipHint, type DField } from "./titleDraft";
import styles from "./TitleEditContent.module.scss";

interface FieldInputProps {
  field: DField;
  onChange: (next: DField) => void;
  testId: string;
  ariaLabel: string;
  placeholder?: string;
  autoFocus?: boolean;
  strong?: boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  /** Right-hand controls, inside the field's frame (a remove button). */
  children?: ReactNode;
}

/**
 * One line of the title block. Kept commands (\orcidlink, \href, macros) show
 * as atomic chips beside the text: they can be deleted, never edited.
 */
export function FieldInput({
  field,
  onChange,
  testId,
  ariaLabel,
  placeholder,
  autoFocus,
  strong,
  onKeyDown,
  onFocus,
  children,
}: FieldInputProps) {
  return (
    <div className={styles.field} data-strong={strong ? "true" : undefined}>
      <input
        type="text"
        data-testid={testId}
        data-field-id={field.id}
        aria-label={ariaLabel}
        className={styles.fieldInput}
        value={field.text}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onChange={(e) => onChange({ ...editFieldText(field, e.target.value), id: field.id })}
        onKeyDown={onKeyDown}
      />
      {field.chips.map((chip, i) => (
        <span
          key={`${i}-${chip.raw}`}
          className={styles.chip}
          data-testid={testIds.titleEdit.keptChip}
          title={chipHint(chip.raw)}
        >
          {/^\\[A-Za-z]+/.exec(chip.raw)?.[0] ?? chip.raw}
          <button
            type="button"
            className={styles.chipRemove}
            data-testid={testIds.titleEdit.keptChipRemove}
            aria-label={`Remove ${chip.raw}`}
            onClick={() => onChange({ ...field, chips: field.chips.filter((_, j) => j !== i) })}
          >
            ✕
          </button>
        </span>
      ))}
      {children}
    </div>
  );
}
